/**
 * @fileoverview Gerenciador de contexto global — versão simplificada.
 * Com Redux como single source of truth, este módulo serve apenas para:
 * - forceUpdate: re-busca dados do servidor → localStorage (o middleware persiste no Redux)
 * - getSummary/getCacheStats: utilitários de debug
 *
 * @module globalContextManager
 * @since 2.0.0
 */

import globalContextService from '../services/globalContextService.js';
import { store } from '../store/store.js';
import {
  setProducts,
  setCategories,
  setBrands,
  setPromotions,
} from '../store/slices/globalDataSlice.js';

/**
 * Mapeia tipo de dado para action Redux e método de fetch.
 */
const DATA_MAP = {
  products: { action: setProducts, fetch: () => globalContextService.fetchAndStoreProducts() },
  categories: { action: setCategories, fetch: () => globalContextService.fetchAndStoreCategories() },
  brands: { action: setBrands, fetch: () => globalContextService.fetchAndStoreBrands() },
  promotions: { action: setPromotions, fetch: () => globalContextService.fetchAndStorePromotions() },
};
 
class GlobalContextManager {
  /**
   * Obtém dados do Redux store (single source of truth).
   * @param {string} dataType - Tipo de dados
   * @returns {Array|null}
   */
  getCachedData(dataType) {
    const state = store.getState().globalData;
    return state[dataType] || null;
  }

  /**
   * Invalida cache — agora é no-op pois Redux é reativo.
   * Mantido para compatibilidade com código existente.
   * @param {string} [dataType]
   */
  invalidateCache(dataType = null) {
    console.log(`🗑️ Cache invalidado (no-op, Redux é reativo): ${dataType || 'todos'}`);
  }

  /**
   * Força atualização de um tipo de dados: servidor → localStorage → Redux.
   * @async
   * @param {string} dataType - Tipo de dados a atualizar
   * @returns {Promise<boolean>} Sucesso na operação
   */
  async forceUpdate(dataType) {
    const mapping = DATA_MAP[dataType];
    if (!mapping) {
      console.error(`Tipo de dados inválido: ${dataType}`);
      return false;
    }

    try {
      const success = await mapping.fetch();
      if (success) {
        const data = globalContextService.getLocalData(dataType);
        if (data) {
          store.dispatch(mapping.action(data));
        }
      }
      return success;
    } catch (error) {
      console.error(`Erro ao forçar atualização de ${dataType}:`, error);
      return false;
    }
  }

  /**
   * Verifica se há dados disponíveis.
   * @param {string} dataType
   * @returns {boolean}
   */
  hasData(dataType) {
    const data = this.getCachedData(dataType);
    return Array.isArray(data) && data.length > 0;
  }

  /**
   * Obtém contagem de itens.
   * @param {string} dataType
   * @returns {number}
   */
  getCount(dataType) {
    const data = this.getCachedData(dataType);
    return data ? data.length : 0;
  }

  /**
   * Obtém resumo de todos os dados.
   * @returns {Object}
   */
  getSummary() {
    return {
      products: this.getCount('products'),
      categories: this.getCount('categories'),
      brands: this.getCount('brands'),
      promotions: this.getCount('promotions'),
      cache: this.getCacheStats(),
    };
  }

  /**
   * Obtém estatísticas do cache (agora baseado no Redux).
   * @returns {Object}
   */
  getCacheStats() {
    const state = store.getState().globalData;
    return {
      isValid: true,
      lastUpdate: state.lastSync,
      age: state.lastSync ? Date.now() - new Date(state.lastSync).getTime() : null,
      ttl: null,
      cachedTypes: ['products', 'categories', 'brands', 'promotions'].filter(
        (key) => state[key]?.length > 0
      ),
    };
  }
}

export default new GlobalContextManager();
